import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IoMdNotifications } from 'react-icons/io'

import { NotificationsModal } from './modal'
import { NotificationsPanel } from './panel'
import { NotificationsContainer } from './styles'
import { useNotifications } from './useNotifications'

/**
 * Centro de notificações do cabeçalho.
 *
 * O sino abre um painel curto com as mais recentes. Tudo que pede espaço — a lista completa e o
 * texto integral de uma notificação — acontece no modal, que o painel abre e sai da frente.
 */
export const NotificationsCenter: React.FC = () => {
  const feed = useNotifications()

  const [panelOpen, setPanelOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [focusedId, setFocusedId] = useState<number | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  /* O cartão sai do sino para a esquerda. Em tela estreita o cabeçalho quebra em duas linhas e
     o sino vai parar perto da borda, de onde o cartão ficaria metade fora da tela. */
  useLayoutEffect(() => {
    const card = cardRef.current

    if (!panelOpen || !card) {
      return
    }

    card.style.setProperty('--shift', '0px')

    const shift = Math.max(0, 8 - card.getBoundingClientRect().left)

    card.style.setProperty('--shift', `${shift}px`)
  }, [panelOpen])

  useEffect(() => {
    if (!panelOpen) {
      return
    }

    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setPanelOpen(false)
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setPanelOpen(false)
        triggerRef.current?.focus()
      }
    }

    /* Sem fechar no scroll, ao contrário do menu de conta: o painel está ancorado no cabeçalho,
       que não rola, e a própria lista de notificações rola por dentro. */
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [panelOpen])

  /** O painel não divide a tela com o modal: quem abre o modal fecha o painel. */
  function openModal(id: number | null) {
    setFocusedId(id)
    setModalOpen(true)
    setPanelOpen(false)

    if (id) {
      feed.markAsRead(id)
    }
  }

  const { unreadCount } = feed

  return (
    <NotificationsContainer ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`notifications__trigger ${panelOpen ? 'is-open' : ''}`}
        aria-label={
          unreadCount > 0
            ? `Notificações (${unreadCount} não ${
                unreadCount === 1 ? 'lida' : 'lidas'
              })`
            : 'Notificações'
        }
        aria-haspopup="dialog"
        aria-expanded={panelOpen}
        onClick={() => setPanelOpen(!panelOpen)}
      >
        <IoMdNotifications />
        {unreadCount > 0 && (
          <span className="notifications__badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {panelOpen && (
        <div
          className="notifications__card"
          ref={cardRef}
          role="dialog"
          aria-label="Notificações"
        >
          <NotificationsPanel
            feed={feed}
            onOpenAll={() => openModal(null)}
            onOpenNotification={openModal}
          />
        </div>
      )}

      {modalOpen && (
        <NotificationsModal
          feed={feed}
          focusedId={focusedId}
          onHide={() => setModalOpen(false)}
        />
      )}
    </NotificationsContainer>
  )
}
